<template>
    <div v-if="!isMobile">
        <ul class="core-menu">
            <li
                v-for="menuItem in filteredMenuItems"
                v-show="menuItem.string !== 'dashboard'"
                :key="menuItem.string"
                class="list-item"
                @mouseenter="handleItemHovered(true)"
                @mouseleave="handleItemHovered(false)"
            >
                <component :is="el(menuItem)" v-bind="props(menuItem)" class="menu-link">
                    <span>{{ menuItem.name }}</span>
                </component>
                <transition name="menu-item">
                    <ul v-if="menuItem.subnav && subnavExists(menuItem.subnav) && isHovered" class="core-menu-list">
                        <li v-for="(value, key) in menuItem.subnav" :key="key">
                            <CoreMenuItem :item="value"></CoreMenuItem>
                        </li>
                    </ul>
                </transition>
            </li>
        </ul>
    </div>
    <div v-else class="mobile-wrapper">
        <CoreAccordion
            v-for="(menuItem, index) in menu"
            v-show="menuItem.string !== 'dashboard'"
            :key="menuItem.string"
            v-model="mobileItemOpen[index]"
            :showToggle="showToggle(menuItem)"
            toggleMode="header"
        >
            <template #title>
                <component
                    :is="el(menuItem)"
                    v-if="!menuItem.subnav"
                    v-bind="props(menuItem)"
                    class="core-menu-list-title"
                    @click="menuClickHandler($event, menuItem)"
                >
                    <span>{{ menuItem.name }}</span>
                </component>
                <a v-else class="core-menu-list-title" @click="menuClickHandler($event, menuItem)">
                    <span>{{ menuItem.name }}</span>
                </a>
            </template>
            <template #body>
                <ul v-if="menuItem.subnav" class="core-menu-list-mobile">
                    <li
                        v-for="item in filterMenuItemsDictionary(menuItem.subnav)"
                        :key="item.string"
                        class="sub-item"
                        :class="{ active: isSubNavActive(item) }"
                        @click="menuItemClickHandler"
                    >
                        <CoreMenuItem v-if="hideOnMobile(item)" :item="item" color="light"></CoreMenuItem>
                    </li>
                </ul>
            </template>
        </CoreAccordion>
    </div>
</template>

<script lang="ts">
    import { Component, Emit, Prop, Watch, Mixins } from 'vue-property-decorator';
    import { TopNavItemInterface } from '@/common/interfaces';
    import CoreMenuItem from './CoreMenuItem.vue';
    import { isPossiblyMobileDeviceWithSmallScreen } from '@/common/services/mobile-fallback';
    import CoreAccordion from '@/common/components/accordion/CoreAccordion.vue';
    import { MobileModeStoreMixin } from '@/common/store/mobile-mode';

    @Component({
        components: {
            CoreMenuItem,
            CoreAccordion,
        },
    })
    export default class CoreMenu extends Mixins(MobileModeStoreMixin) {
        @Prop({ default: {} }) public menuItems!: { [name: string]: TopNavItemInterface };
        @Prop({ default: 'horizontal' }) public mode!: 'inline' | 'horizontal';

        public mobileItemOpen: boolean[] = [];
        public isHovered = false;

        @Watch('menuItems')
        public setMobileItemOpen() {
            this.mobileItemOpen = Object.keys(this.menuItems).map(() => false);
        }

        public handleItemHovered(state: boolean) {
            this.isHovered = state;
        }

        public menuClickHandler(e: Event, item) {
            if (item.subnav?.length !== 0) {
                e.preventDefault();
            }
        }
        @Emit('item-click')
        public menuItemClickHandler() {
            return;
        }

        public subnavExists(subnav) {
            return subnav && typeof subnav === 'object' && Object.keys(subnav).length > 0;
        }

        public subItems(item): TopNavItemInterface[] {
            return Object.values(item.subnav ?? {});
        }

        public showToggle(menuItem) {
            return this.subItems(menuItem).length < 1 ? false : true;
        }

        public hideOnMobile(item) {
            return !(isPossiblyMobileDeviceWithSmallScreen() && item.noMobileSupport);
        }

        public get menu() {
            return Object.values(this.menuItems)
                .filter(item => item.access)
                .filter(item => {
                    return !(isPossiblyMobileDeviceWithSmallScreen() && item.noMobileSupport);
                });
        }

        public filterMenuItemsDictionary(obj: { [name: string]: TopNavItemInterface }): {
            [name: string]: TopNavItemInterface;
        } {
            if (!obj) return {};

            return Object.entries<TopNavItemInterface>(obj)
                .filter(([_, item]) => item.access)
                .reduce((acc, [key, value]) => {
                    acc[key] = value;
                    return acc;
                }, {});
        }

        public get filteredMenuItems(): { [name: string]: TopNavItemInterface } {
            return this.filterMenuItemsDictionary(this.menuItems);
        }

        public el(item) {
            return item.vueRoute ? 'router-link' : 'a';
        }

        public isSubNavActive(item) {
            if (item.route.includes('app')) {
                const to = item.route.split('app')[1];
                return to === this.$route.path || to === this.$route.query?.from;
            }
            return item.route.endsWith(this.$route.path);
        }

        public props(item) {
            if (item.vueRoute) {
                try {
                    return {
                        to: { path: item.route.replace('/app/', '/') },
                    };
                } catch (error) {
                    return { to: item.route };
                }
            } else {
                return { href: item.route };
            }
        }
    }
</script>

<style lang="less" scoped>
    .menu-item-enter-active,
    .menu-item-leave-active {
        transition: @generic-transition;
    }

    .menu-item-enter,
    .menu-item-leave-to {
        opacity: 0;
    }
    .sub-item {
        display: flex;
        align-items: center;
        min-height: 56px;
        margin-bottom: @spacing-2;
        background-color: @bright-color-8;
        &:first-of-type {
            border-top-left-radius: @border-radius-8;
            border-top-right-radius: @border-radius-8;
        }
        &:last-of-type {
            border-bottom-right-radius: @border-radius-8;
            border-bottom-left-radius: @border-radius-8;
        }
        &.active {
            background-color: @bright-color-20;
        }
    }
    .core-menu {
        display: flex;
        flex-direction: row;
        align-items: center;
        width: 100%;
        height: @core-header-height;
        margin: 0;
        padding: 0;
        list-style: none;
        outline: none;

        > .list-item > a {
            color: @branding-text-color;

            &.menu-link {
                .h4-semibold();

                line-height: @core-header-height;
            }
        }

        &:hover > .list-item > a {
            color: @branding-text-color-40;
        }

        & .list-item {
            position: relative;
            display: inline-block;
            height: @core-header-height;
            padding-right: @spacing-20;
            padding-left: @spacing-20;
            color: @branding-text-color-40;
            white-space: pre;
            vertical-align: bottom;

            &:hover {
                > .core-menu-list {
                    display: block;
                }

                .menu-link {
                    color: @branding-text-color;
                    cursor: pointer;
                }
            }

            .core-menu-list {
                position: absolute;
                top: 72px;
                left: 0;
                display: none;
                flex-direction: column;
                min-width: 240px;
                padding: 0 @spacing-8 0 @spacing-8;
                padding-top: @spacing-16;
                padding-bottom: @spacing-16;
                list-style: none;
                background: @bright-color;
                border-radius: @border-radius-8;
                box-shadow: 0 2px 40px 0 @dark-color-8;
            }
        }
    }

    .mobile-wrapper {
        display: flex;
        flex-direction: column;
        width: 100%;
        height: 100%;
        min-height: calc(100vh - @core-header-height);
        padding: @spacing-20;
        overflow: auto;
        background-color: @branding-color;

        & ::v-deep .wrapper {
            border-bottom: 1px solid @dark-color-8;

            &:last-child {
                border-bottom: 1px solid transparent;
            }
        }

        & ::v-deep .wrapper.active {
            & .header .core-menu-list-title {
                color: @branding-text-color;
            }

            & .header i {
                color: @branding-text-color;
            }

            & .header i::before {
                margin-left: @spacing-4;
            }
        }

        ::v-deep .header {
            display: flex;
            align-items: center;
            justify-content: flex-start;
            height: 56px;
            line-height: 56px;
            background-color: @branding-color;

            .core-menu-list-title {
                .h4();

                display: flex;
                align-items: center;
                width: 97%;
                height: 56px;
                padding-left: @spacing-16;
                color: @branding-text-color-40;
                line-height: 56px;
            }

            & i {
                margin-right: @spacing-20;
                color: @branding-text-color-40;
            }

            & i::before {
                font-size: 16px;
            }
        }

        .core-menu-list-mobile {
            display: flex;
            flex-direction: column;
            width: 100%;
            margin: 0;
            padding: 0;
            list-style: none;
        }
    }
</style>
